import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { Link, redirect, useActionData, useLoaderData, useNavigate, useNavigation, useSubmit } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { ApiError, api } from '../lib/api';
import type { AuthUser, StudioInvitation, StudioMembership } from '../lib/types';
import StudioArt from '../components/StudioArt';
import '../styles/auth.css';
import '../styles/studios.css';

interface StudiosLoaderData {
  user: AuthUser;
  next: string;
  studios: StudioMembership[];
  invitations: StudioInvitation[];
  chosen: boolean;
}

interface StudiosActionData {
  error?: string;
  id?: string;
}

const safeNext = (value: string | null): string => (value && /^\/(?!\/)[A-Za-z0-9/_?=&.-]*$/.test(value) && !value.startsWith('/studios') ? value : '/');
const title = (value: string) => value ? value[0].toUpperCase() + value.slice(1) : value;
const reducedMotion = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
const TRANSITION_MS = 760;

export async function studiosLoader({ request }: LoaderFunctionArgs): Promise<StudiosLoaderData | Response> {
  const url = new URL(request.url);
  const next = safeNext(url.searchParams.get('next'));
  const auth = await api.authState();
  if (!auth.authenticated || !auth.user) return redirect(`/signin?next=${encodeURIComponent(`/studios${url.search}`)}`);
  const studios = auth.user.studios || [];
  const invitations = auth.user.invitations || [];
  if (studios.length < 2 && !invitations.length) return redirect(next);
  return { user: auth.user, next, studios, invitations, chosen: Boolean(auth.studioChosen) };
}

export async function studiosAction({ request }: ActionFunctionArgs): Promise<StudiosActionData | Response> {
  const form = await request.formData();
  const intent = String(form.get('intent') || '');
  const id = String(form.get('id') || '');
  const next = safeNext(String(form.get('next') || '/'));
  try {
    if (intent === 'choose') {
      await api.switchStudio(id);
      return redirect(next);
    }
    if (intent === 'accept') {
      await api.acceptStudioInvitation(id);
      return redirect(next);
    }
    if (intent === 'decline') {
      await api.declineStudioInvitation(id);
      return {};
    }
    return { error: 'Unknown request.', id };
  } catch (error) {
    return { error: error instanceof ApiError || error instanceof Error ? error.message : 'Could not open that studio.', id };
  }
}

export default function Studios() {
  const { user, next, studios, invitations, chosen } = useLoaderData() as StudiosLoaderData;
  const actionData = useActionData() as StudiosActionData | undefined;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const sceneRef = useRef<HTMLElement>(null);
  const cards = useRef<(HTMLButtonElement | null)[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [iris, setIris] = useState<{ x: number; y: number } | null>(null);
  const current = chosen ? studios.find(studio => studio.active) : undefined;
  const busy = navigation.state !== 'idle' || picked !== null;

  useEffect(() => {
    const frame = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!actionData?.error) return;
    setPicked(null);
    setIris(null);
  }, [actionData]);

  const send = (intent: 'choose' | 'accept' | 'decline', id: string) => submit({ intent, id, next }, { method: 'post' });

  function choose(studio: StudioMembership, element: HTMLElement | null) {
    if (busy) return;
    const box = element?.getBoundingClientRect();
    if (box) setIris({ x: box.left + box.width / 2, y: box.top + box.height / 2 });
    setPicked(studio.id);
    const go = () => (studio.active && chosen ? navigate(next, { replace: true }) : send('choose', studio.id));
    if (reducedMotion()) go();
    else window.setTimeout(go, TRANSITION_MS);
  }

  const chooseRef = useRef(choose);
  useEffect(() => { chooseRef.current = choose; });

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      if (event.target instanceof HTMLElement && (event.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName))) return;
      const digit = Number(event.key);
      if (!Number.isInteger(digit) || digit < 1 || digit > Math.min(9, studios.length)) return;
      event.preventDefault();
      chooseRef.current(studios[digit - 1], cards.current[digit - 1]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [studios]);

  function onGridKey(event: KeyboardEvent<HTMLElement>) {
    const index = cards.current.findIndex(card => card === document.activeElement);
    if (index < 0) return;
    const columns = Math.max(1, Math.round((sceneRef.current?.querySelector('.sc-grid')?.clientWidth || 1) / ((cards.current[0]?.offsetWidth || 1) + 16)));
    const moves: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns, Home: -index, End: studios.length - 1 - index };
    if (!(event.key in moves)) return;
    event.preventDefault();
    cards.current[Math.min(studios.length - 1, Math.max(0, index + moves[event.key]))]?.focus();
  }

  function tilt(event: PointerEvent<HTMLButtonElement>) {
    if (reducedMotion()) return;
    const box = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--tx', ((event.clientX - box.left) / box.width - 0.5).toFixed(3));
    event.currentTarget.style.setProperty('--ty', ((event.clientY - box.top) / box.height - 0.5).toFixed(3));
    const scene = sceneRef.current?.getBoundingClientRect();
    if (scene) {
      sceneRef.current?.style.setProperty('--mx', `${(((event.clientX - scene.left) / scene.width) * 100).toFixed(1)}%`);
      sceneRef.current?.style.setProperty('--my', `${(((event.clientY - scene.top) / scene.height) * 100).toFixed(1)}%`);
    }
  }

  const untilt = (event: PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.style.setProperty('--tx', '0');
    event.currentTarget.style.setProperty('--ty', '0');
  };

  return (
    <main ref={sceneRef} className="sa-scene sc-scene" data-loaded={loaded || undefined} data-picking={picked ? '' : undefined}>
      <div className="sa-bg" aria-hidden="true">
        <div className="sa-aurora" />
        <div className="sa-spot" />
        <div className="sa-grid" />
        <div className="sa-grain" />
      </div>
      <div className="sa-letterbox sa-letterbox--top" aria-hidden="true" />
      <div className="sa-letterbox sa-letterbox--bottom" aria-hidden="true" />
      <div className="sa-frame" aria-hidden="true"><i /><i /><i /><i /></div>

      <header className="sa-top">
        <span className="sa-brand">
          <img src="/brand/frameon-mark.svg" alt="" width={16} height={20} />
          <span>Frameon</span>
        </span>
        <div className="sc-account">
          <span className="sa-mono" title={user.email}>{user.email}</span>
          <Link className="sa-link" to="/signout">Sign out</Link>
        </div>
      </header>

      <section className="sc-body" aria-labelledby="sc-title">
        <div className="sc-intro">
          <p className="sa-mono sc-eyebrow">Call sheet · {studios.length} {studios.length === 1 ? 'studio' : 'studios'}{invitations.length ? ` · ${invitations.length} waiting` : ''}</p>
          <h1 id="sc-title" className="sa-display sc-display">
            <span className="sa-mask" style={{ '--i': 0 } as CSSProperties}><span>Which stage</span></span>
            <span className="sa-mask sc-ghost" style={{ '--i': 1 } as CSSProperties}><span>today?</span></span>
          </h1>
          <p className="sa-lede sc-lede">
            You are on the crew at more than one studio. Each keeps its own productions, provider keys and spend — pick one to walk onto its floor.
          </p>
        </div>

        {actionData?.error ? (
          <div className="sc-error" role="alert">
            <strong>Could not open that studio.</strong> {actionData.error}
          </div>
        ) : null}

        <ul className="sc-grid" aria-label="Your studios" onKeyDown={onGridKey}>
          {studios.map((studio, index) => {
            const isCurrent = Boolean(current && current.id === studio.id);
            const state = picked === studio.id ? 'picked' : picked ? 'dismissed' : actionData?.error && actionData.id === studio.id ? 'failed' : undefined;
            return (
              <li key={studio.id} style={{ '--i': index } as CSSProperties}>
                <button
                  type="button"
                  ref={element => { cards.current[index] = element; }}
                  className="sc-card"
                  data-state={state}
                  data-current={isCurrent || undefined}
                  disabled={busy && picked !== studio.id}
                  aria-label={`Open ${studio.name}, ${title(studio.role)}${isCurrent ? ', current studio' : ''}. Shortcut ${index + 1}.`}
                  onClick={event => choose(studio, event.currentTarget)}
                  onPointerMove={tilt}
                  onPointerLeave={untilt}
                >
                  <span className="sc-card-art">
                    <StudioArt studioId={studio.id} name={studio.name} index={index} />
                    <span className="sc-card-flash" aria-hidden="true" />
                  </span>
                  <span className="sc-card-meta">
                    <span className="sc-card-row">
                      <span className="sa-mono">Stage {String(index + 1).padStart(2, '0')}</span>
                      {index < 9 ? <kbd className="sc-kbd" aria-hidden="true">{index + 1}</kbd> : null}
                    </span>
                    <strong className="sc-card-name">{studio.name}</strong>
                    <span className="sc-card-row">
                      <span className="sc-chip">{title(studio.role)}</span>
                      {isCurrent ? <span className="sc-chip is-current">Current</span> : null}
                      <span className="sc-enter" aria-hidden="true">Enter →</span>
                    </span>
                  </span>
                  <span className="sc-brackets" aria-hidden="true"><i /><i /><i /><i /></span>
                </button>
              </li>
            );
          })}
        </ul>

        {invitations.length ? (
          <section className="sc-invites" aria-labelledby="sc-invites-title">
            <h2 id="sc-invites-title" className="sa-mono sc-section-title">Waiting on you · {invitations.length} {invitations.length === 1 ? 'invitation' : 'invitations'}</h2>
            <ul className="sc-grid">
              {invitations.map((invite, index) => (
                <li key={invite.id} style={{ '--i': studios.length + index } as CSSProperties}>
                  <article className="sc-card sc-card--invite" data-state={actionData?.error && actionData.id === invite.id ? 'failed' : undefined} aria-label={`Invitation to ${invite.studioName}`}>
                    <span className="sc-card-art">
                      <StudioArt studioId={invite.studioId} name={invite.studioName} index={studios.length + index} muted />
                    </span>
                    <span className="sc-card-meta">
                      <span className="sa-mono">{invite.invitedBy ? `Invited by ${invite.invitedBy}` : 'Invitation'}</span>
                      <strong className="sc-card-name">{invite.studioName}</strong>
                      <span className="sc-card-row">
                        <span className="sc-chip">Join as {invite.role}</span>
                      </span>
                      <span className="sc-invite-actions">
                        <button type="button" className="sc-accept" disabled={busy} onClick={() => { setPicked(invite.id); send('accept', invite.id); }}>Accept &amp; enter</button>
                        <button type="button" className="sa-link" disabled={busy} onClick={() => send('decline', invite.id)}>Decline</button>
                      </span>
                    </span>
                  </article>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <footer className="sc-foot">
          <p className="sa-mono sc-hint" aria-hidden="true">
            <kbd className="sc-kbd">←</kbd><kbd className="sc-kbd">→</kbd> move · <kbd className="sc-kbd">1</kbd>–<kbd className="sc-kbd">{Math.min(9, studios.length)}</kbd> jump · <kbd className="sc-kbd">↵</kbd> enter
          </p>
          {current ? (
            <button type="button" className="sa-link" disabled={busy} onClick={() => navigate(next, { replace: true })}>Stay in {current.name}</button>
          ) : (
            <p className="sa-mono sc-hint">Change studio any time from the account menu.</p>
          )}
        </footer>
      </section>

      {iris ? <div className="sc-iris" aria-hidden="true" style={{ '--ix': `${iris.x}px`, '--iy': `${iris.y}px` } as CSSProperties} /> : null}
      <p className="sc-live" role="status" aria-live="polite">{picked ? `Opening ${studios.find(studio => studio.id === picked)?.name || invitations.find(invite => invite.id === picked)?.studioName || 'studio'}…` : ''}</p>
    </main>
  );
}
